import { useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../../../../../backend/src/shared/action-registry';
import { ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { useGetFactDetailsBatch } from '@/api/queries/useGetFactDetailsBatch';
import { FactDetails } from '@/api/types/backend-dto-types';
import { PermissionGate } from '@/hooks/useHasPermission';
import { CopyIconButton } from '@/new/components/copy-icon-button';
import { DocChronologyDropdown } from '@/new/components/doc-chronology-dropdown';
import { FactAccordionHeader } from '@/new/components/fact/fact-accordion';
import { FactChronologyDropdown } from '@/new/components/fact-chronology-dropdown';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SubsetDropdown } from '@/new/components/subset-dropdown';

function FactReferences({
  subsetId,
  facts,
  isLoading,
  factIds,
  selectedFactIds,
  setSelectedFactIds,
  referenceIndexMap,
  onReferenceClick,
}: {
  subsetId: string;
  facts: FactDetails[];
  isLoading: boolean;
  factIds: string[];
  selectedFactIds: Set<string>;
  setSelectedFactIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
}) {
  const allRowsChecked = facts.length > 0 && selectedFactIds.size === facts.length;

  const toggleAllHandler = () => {
    if (allRowsChecked) {
      setSelectedFactIds(new Set());
    } else {
      setSelectedFactIds(new Set(facts.map((f) => f.id)));
    }
  };

  return (
    <>
      {!isLoading && (
        <div className="mt-4 flex flex-wrap items-center gap-2 gap-y-4 pr-2 pl-5.25">
          <div className="flex grow items-center gap-3 text-xs text-gray-500">
            <Checkbox checked={allRowsChecked} onCheckedChange={toggleAllHandler} />
            <p>
              <strong>{selectedFactIds.size.toLocaleString()}</strong> Facts selected
            </p>

            <Button
              size="sm"
              variant={'ghost'}
              disabled={selectedFactIds.size === 0}
              onClick={() => setSelectedFactIds(new Set())}
              className="gap-1"
            >
              <Icon className="lucide--x" />
              Clear all
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 px-1">
        {isLoading &&
          Array.from({ length: Math.min(factIds.length, 3) }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl bg-gray-100" />
          ))}

        {!isLoading &&
          facts.map((fact) => (
            <div key={fact.id} className="flex items-start gap-3">
              <button
                data-state="closed"
                className="group min-w-0 flex-1 cursor-pointer rounded-xl border bg-white px-4 py-3 pt-2.75 pr-3 text-left text-[13px] ring-offset-2 transition-colors duration-150 hover:border-gray-300 focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:outline-none"
                onClick={() => onReferenceClick?.(fact.id, ReferenceType.Fact)}
              >
                <FactAccordionHeader
                  selectable={{
                    isSelected: selectedFactIds.has(fact.id),
                    handleSelect: (checked) => {
                      setSelectedFactIds((prev) => {
                        const next = new Set(prev);
                        if (checked) {
                          next.add(fact.id);
                        } else {
                          next.delete(fact.id);
                        }
                        return next;
                      });
                    },
                  }}
                  reference={referenceIndexMap.get(fact.id)?.index}
                  editable={false}
                  onViewDocument={() => {
                    onReferenceClick?.(fact.id, ReferenceType.Fact);
                  }}
                  fact={fact}
                  subsetId={subsetId}
                />
              </button>
            </div>
          ))}
      </div>
    </>
  );
}

export function EndBlock({
  getTextToCopy,
  factIds,
  referenceIndexMap,
  onReferenceClick,
  onDownloadClick,
  isDownloading,
  isChronology = false,
}: {
  getTextToCopy: () => string;
  factIds: string[];
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  onDownloadClick?: () => void;
  isDownloading?: boolean;
  isChronology?: boolean;
}) {
  const { matterId, subsetId } = useParams();
  const navigate = useNavigate();
  const [showFactReferences, setShowFactReferences] = useState(false);
  const [selectedFactIds, setSelectedFactIds] = useState<Set<string>>(new Set(factIds));

  const { isLoading, data } = useGetFactDetailsBatch(matterId, subsetId, factIds);
  const facts = data ?? [];

  const docIds = useMemo(() => {
    const ids = new Set<string>();
    for (const fact of facts) {
      for (const location of fact.locations) {
        ids.add(location.docId);
      }
    }
    return Array.from(ids);
  }, [facts]);

  return (
    <div>
      <motion.div
        className="mx-auto mt-6 flex w-full max-w-3xl items-start justify-between gap-4 px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15, delay: 0.2 }}
      >
        <div className="mt-0.5 flex gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <CopyIconButton getText={getTextToCopy} />
            </TooltipTrigger>

            <TooltipContent>Copy answer to clipboard</TooltipContent>
          </Tooltip>

          <PermissionGate
            permission={Permission.ThreadDownload}
            uiMode="disabled"
            disabledMessage="You don't have permission to download an answer"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => onDownloadClick?.()}
                  disabled={isDownloading}
                  variant="ghost"
                  className="p-1 px-1.5"
                >
                  <Icon className="lucide--arrow-down-to-line" />
                </Button>
              </TooltipTrigger>

              <TooltipContent>Download as Word document</TooltipContent>
            </Tooltip>
          </PermissionGate>
        </div>

        <AnimatePresence>
          {factIds.length > 0 && !isLoading && matterId && subsetId && (
            <motion.div
              className="flex flex-wrap items-center justify-end gap-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15 }}
            >
              {isChronology ? (
                <FactChronologyDropdown
                  matterId={matterId}
                  subsetId={subsetId}
                  selectedFactIds={selectedFactIds}
                  createTitlePrefix="From Kim: "
                  triggerText="Save as a chronology in Wexler"
                  size="sm"
                  variant="white"
                />
              ) : (
                <>
                  <SubsetDropdown
                    matterId={matterId}
                    subsetId={subsetId}
                    docIds={docIds}
                    size="sm"
                    variant="white"
                    triggerText="Subset"
                    onCreateNew={() => navigate(`/matters/${matterId}/subsets/new?documentIds=${docIds.join(',')}`)}
                  />

                  <FactChronologyDropdown
                    matterId={matterId}
                    subsetId={subsetId}
                    selectedFactIds={selectedFactIds}
                    createTitlePrefix="From Kim: "
                    triggerText="Fact Chronology"
                    size="sm"
                    variant="white"
                  />

                  <DocChronologyDropdown
                    matterId={matterId}
                    subsetId={subsetId}
                    selectedDocIds={new Set(docIds)}
                    size="sm"
                    triggerText="Document Chronology"
                    createDescription="Create a new document chronology from the source documents referenced in this answer"
                    addDescription="Add the source documents referenced in this answer to an existing document chronology"
                    variant="white"
                  />

                  <Button
                    variant={'white'}
                    size={'sm'}
                    className="group text-gray-600"
                    onClick={() => setShowFactReferences((prev) => !prev)}
                  >
                    {showFactReferences ? 'Hide' : 'View'} references
                    <Icon className="lucide--book-open" />
                  </Button>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {!isChronology && showFactReferences && matterId && subsetId && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mx-auto max-w-3xl overflow-hidden px-px"
          >
            <FactReferences
              subsetId={subsetId}
              facts={facts}
              isLoading={isLoading}
              factIds={factIds}
              selectedFactIds={selectedFactIds}
              setSelectedFactIds={setSelectedFactIds}
              referenceIndexMap={referenceIndexMap}
              onReferenceClick={onReferenceClick}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
